import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController, LineElement, PointElement,
  BarController, BarElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale, TimeScale,
  Tooltip, Legend, Filler
} from "chart.js"

Chart.register(
  LineController, LineElement, PointElement,
  BarController, BarElement,
  DoughnutController, ArcElement,
  CategoryScale, LinearScale, TimeScale,
  Tooltip, Legend, Filler
)

const MONO = '"IBM Plex Mono", ui-monospace, monospace'

// Per-theme chart colors. `dark` reproduces the original Monaco chart values
// byte-for-byte, so dark charts are unchanged; `light` is the editorial-light
// counterpart (deeper text + lighter grid for contrast on the warm-white
// canvas). The active palette is chosen off the <html> theme class.
const THEMES = {
  dark: {
    text: "#97A3B8",
    tick: "#5B6678",
    grid: "#1B212C",
    border: "#252D3B",
    tooltipBg: "#141923",
    tooltipTitle: "#E7ECF4",
    tooltipBody: "#97A3B8"
  },
  light: {
    text: "#6A6A66",
    tick: "#9A9A95",
    grid: "#ECECE8",
    border: "#E4E4DF",
    tooltipBg: "#FFFFFF",
    tooltipTitle: "#1A1A1F",
    tooltipBody: "#6A6A66"
  }
}

function currentPalette() {
  return document.documentElement.classList.contains("light") ? THEMES.light : THEMES.dark
}

Chart.defaults.font.family = MONO
Chart.defaults.font.size = 10.5

// Renders a Chart.js chart from data attributes:
//   data-controller="chart"
//   data-chart-type-value="line|bar|doughnut"
//   data-chart-data-value='{"labels": [...], "datasets": [...]}'
//   data-chart-options-value='{...}'   (optional, merged over defaults)
export default class extends Controller {
  static values = {
    type: { type: String, default: "line" },
    data: Object,
    options: { type: Object, default: {} }
  }

  connect() {
    this.canvas = this.element.tagName === "CANVAS"
      ? this.element
      : this.element.querySelector("canvas")

    this.render()

    // Re-render on theme switch so chart colors track the active palette.
    this.onThemeChange = () => this.render()
    window.addEventListener("theme:changed", this.onThemeChange)
  }

  disconnect() {
    window.removeEventListener("theme:changed", this.onThemeChange)
    this.chart?.destroy()
  }

  render() {
    this.chart?.destroy()

    const palette = currentPalette()
    Chart.defaults.color = palette.text
    Chart.defaults.borderColor = palette.border

    this.chart = new Chart(this.canvas, {
      type: this.typeValue,
      data: this.dataValue,
      options: { ...this.baseOptions(palette), ...this.optionsValue }
    })
  }

  baseOptions(palette) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          borderColor: palette.border,
          borderWidth: 1,
          titleColor: palette.tooltipTitle,
          bodyColor: palette.tooltipBody,
          cornerRadius: 2,
          titleFont: { family: MONO },
          bodyFont: { family: MONO }
        }
      },
      scales: this.typeValue === "doughnut" ? {} : {
        x: { grid: { color: palette.grid }, ticks: { color: palette.tick } },
        y: { grid: { color: palette.grid }, ticks: { color: palette.tick } }
      }
    }
  }
}
